import React, { useEffect, useCallback, useMemo } from 'react';
import Head from 'next/head';
import { GetServerSideProps } from 'next';
import { ButtonPrimary, Text } from '@spotify-internal/encore-web';
import { extractUserIdNoError } from '../../../../server/lib/userid';
import { getReceiverLandingPage } from '../../../../server/api/referrals-service';
import { getBasePageProps, BasePageProps } from '../../../../lib/basePageProps';
import {
  ReceiverLandingPageErrorState,
  ErrorPageContent,
  ReceiverLandingPageContent,
  ReferralExperiment,
} from '../../../../types/referrals';
import { trackEvent } from '../../../../lib/tracking';
import BasePage from '../../../../components/BasePage';
import { ErrorView } from '../../../../components/ErrorView/ErrorView';
import {
  errorPageViewEvent,
  referralPageViewEvent,
  exposureEvent,
  benefitImpressionEvent,
  clickEvent,
  productClickEvent,
} from '../../../../lib/tracking/eventPayloadDefinitions';
import { PageHero } from '../../../../components/PageHero/PageHero';
import { BenefitsSection } from '../../../../components/BenefitsSection/BenefitsSection';
import { LibraryImporterModule } from '../../../../components/LibraryImporterModule/LibraryImporterModule';
import { SafeHtml } from '../../../../components/SafeHtml/SafeHtml';
import { buildExperimentVariant } from '../../../../lib/tracking/buildExperimentVariant';
import heroStyles from '../../../../components/PageHero/PageHero.module.scss';
import styles from './index.module.scss';

const ERROR_STATE_PAGE_NAME: Record<ReceiverLandingPageErrorState, string> = {
  [ReceiverLandingPageErrorState.COUNTRY_INELIGIBLE]:
    'referrals:error:country-invalid',
};

type BaseReceiverProps = {
  basePageProps: BasePageProps;
  country: string;
  referralCode: string;
  isAuthenticated: boolean;
  shareId?: string;
  activeExperiments: ReferralExperiment[];
};

export type PageProps = BaseReceiverProps &
  (
    | {
        errorState: ReceiverLandingPageErrorState;
        errorPageContent: ErrorPageContent;
        pageContent?: ReceiverLandingPageContent;
      }
    | {
        errorState?: undefined;
        errorPageContent?: undefined;
        pageContent: ReceiverLandingPageContent;
      }
  ) & {
    ogTitle?: string;
    ogDescription?: string;
    ogImage?: string;
  };

const RECEIVER_PAGE = 'referrals:receiver';

export const getServerSideProps: GetServerSideProps<
  PageProps,
  { market: string; code: string }
> = async ({ req, params, query }) => {
  const userId = extractUserIdNoError(req);
  const spotifyMarket = params!.market;

  // Strip accept-language so the backend does not use browser language for translations.
  // Instead, referrals-service uses the market language for consistency with the masthead
  // header and footer. https://spotify.atlassian.net/browse/MOON-3655
  const { 'accept-language': _acceptLang, ...headers } = req.headers;
  const result = await getReceiverLandingPage(
    { referralCode: params!.code, userId, spotifyMarket },
    headers,
  );

  const basePageProps = await getBasePageProps({
    spotifyMarket,
    locale: result.locale,
    userId,
  });

  const isAuthenticated = !!req.headers['sp-internal-user-id'];
  const shareId = typeof query.si === 'string' ? query.si : undefined;

  if (result.kind === 'error') {
    return {
      props: {
        basePageProps,
        country: result.country,
        referralCode: params!.code,
        errorState: result.errorState,
        errorPageContent: result.pageContent,
        isAuthenticated,
        activeExperiments: result.activeExperiments,
        ...(shareId && { shareId }),
      },
    };
  }

  return {
    props: {
      basePageProps,
      country: result.country,
      referralCode: params!.code,
      pageContent: result.pageContent,
      ogTitle: result.pageMetadata?.pageTitle ?? '',
      ogDescription: result.pageMetadata?.pageDescription ?? '',
      ogImage: result.ogImage,
      isAuthenticated,
      activeExperiments: result.activeExperiments,
      ...(shareId && { shareId }),
    },
  };
};

const ReceiverLandingPage = (props: PageProps) => {
  const {
    country,
    referralCode,
    ogTitle,
    ogDescription,
    ogImage,
    isAuthenticated,
    shareId,
    errorState,
    errorPageContent,
    activeExperiments,
  } = props;

  const variant = useMemo(
    () => buildExperimentVariant(activeExperiments),
    [activeExperiments],
  );

  const errorPage = errorState ? ERROR_STATE_PAGE_NAME[errorState] : undefined;

  const handleCtaClick = useCallback(() => {
    const authOptions = { authorize: isAuthenticated };
    trackEvent(
      productClickEvent(
        {
          market: country,
          component: 'continue-cta-v2',
          position: 0,
          link: props.pageContent?.hero.cta?.ctaUri,
          referralCode,
        },
        authOptions,
      ),
    );
    trackEvent(
      clickEvent(
        {
          variant,
          country,
          context: 'referrals-receiver',
          label: 'continue-cta',
        },
        authOptions,
      ),
    );
  }, [country, referralCode, isAuthenticated, props.pageContent, variant]);

  const handleBenefitImpression = useCallback(
    (benefit: string, position: number) => {
      trackEvent(
        benefitImpressionEvent(
          {
            page: RECEIVER_PAGE,
            market: country,
            component: benefit,
            position,
            variant,
          },
          { authorize: isAuthenticated },
        ),
      );
    },
    [country, isAuthenticated, variant],
  );

  useEffect(() => {
    trackEvent(
      exposureEvent(
        { variant, country, context: errorPage ?? 'referrals-receiver' },
        { authorize: isAuthenticated },
      ),
    );

    if (errorPage) {
      trackEvent(
        errorPageViewEvent(
          {
            page: errorPage,
            market: country,
            referralCode,
            share_id: shareId,
          },
          { authorize: isAuthenticated },
        ),
      );
    } else {
      trackEvent(
        referralPageViewEvent(
          {
            page: RECEIVER_PAGE,
            market: country,
            http_referer: document.referrer,
            referralCode,
            share_id: shareId,
          },
          { authorize: isAuthenticated },
        ),
      );
    }
  }, [country, isAuthenticated, referralCode, shareId, errorPage, variant]);

  if (errorPage && errorPageContent) {
    return (
      <BasePage basePageProps={props.basePageProps}>
        <ErrorView
          title={errorPageContent.title}
          subtitle={errorPageContent.subtitle}
          ctaText={errorPageContent.cta?.ctaLabel}
          ctaUrl={errorPageContent.cta?.ctaUri}
        />
      </BasePage>
    );
  }

  const { hero, benefits, libraryImporterModule } = props.pageContent!;

  return (
    <BasePage basePageProps={props.basePageProps}>
      <div className={styles.page}>
        <Head>
          <title>{ogTitle}</title>
          <meta property="og:title" content={ogTitle} />
          <meta property="og:description" content={ogDescription} />
          <meta property="og:image" content={ogImage} />
        </Head>
        <div className={styles.heroWrapper}>
          <PageHero
            title={hero.title}
            subtitle={hero.subtitle}
            contentClassName={styles.heroContent}
          >
            {hero.cta?.ctaLabel && (
              <div className={heroStyles.ctaGroup}>
                <ButtonPrimary
                  size="medium"
                  aria-label={hero.cta.ctaLabel}
                  onClick={handleCtaClick}
                  {...(hero.cta.ctaUri ? { href: hero.cta.ctaUri } : {})}
                >
                  {hero.cta.ctaLabel}
                </ButtonPrimary>
              </div>
            )}
            {hero.legalDisclaimer && (
              <div className={heroStyles.legal}>
                <Text
                  variant="marginal"
                  semanticColor="textSubdued"
                  className={styles.legal}
                >
                  <SafeHtml html={hero.legalDisclaimer} />
                </Text>
              </div>
            )}
          </PageHero>
        </div>
        {libraryImporterModule && (
          <LibraryImporterModule
            libraryImporterModule={libraryImporterModule}
          />
        )}
        <BenefitsSection
          benefits={benefits}
          onBenefitImpression={handleBenefitImpression}
        />
      </div>
    </BasePage>
  );
};

export default ReceiverLandingPage;
